// src/components/feature/BlogSampleOffer.tsx
//
// BLOG-LEAD-MAGNET-SAMPLES-001 — the ONE reusable "free 15-page sample" form
// for blog articles.
//
//   offer="esa"    → ESA Pet Care Planner sample
//   offer="psd"    → PSD Training Workbook sample
//   offer="choice" → the visitor picks; NOTHING is pre-selected
//
// The offer comes from explicit per-article metadata
// (src/data/blogSampleOfferRegistry.ts or the article's own record) — never
// from the URL text or anything the visitor typed.
//
// Asks ONLY for an email address. The marketing box is separate, optional and
// unchecked. No diagnosis or assessment question, no analytics event, no pixel,
// nothing written to the URL. Server-render safe: no window access during
// render; the network call happens only on submit.

import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import {
  CHOICE_OFFER_COPY,
  MARKETING_CONSENT,
  SAMPLE_OFFER_COPY,
  SAMPLE_OFFER_ERRORS,
  SAMPLE_OFFER_FINE_PRINT,
  type SampleVariant,
} from "@/data/blogSampleOffer";
import type { BlogSampleOffer as OfferKind } from "@/data/blogSampleOfferRegistry";
import { requestBlogSample } from "@/lib/blogSampleRequest";

type Status = "idle" | "submitting" | "sent" | "error";

// Deliberately loose — the server is the authority; this only catches typos.
const LOOKS_LIKE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export interface BlogSampleOfferProps {
  offer: OfferKind;
  /** The article route this form sits on (recorded with the request). */
  sourcePath: string;
  /** "inline" inside an article body; "end" below the article. */
  placement?: "inline" | "end";
  presentation?: "inline" | "popup";
}

export default function BlogSampleOffer({ offer, sourcePath, placement = "end", presentation = "inline" }: BlogSampleOfferProps) {
  const popup = presentation === "popup";
  const uid = useId();
  const headingId = `${uid}-heading`;
  const emailId = `${uid}-email`;
  const errorId = `${uid}-error`;
  const consentId = `${uid}-consent`;
  const statusRef = useRef<HTMLHeadingElement>(null);

  const [email, setEmail] = useState("");
  const [variant, setVariant] = useState<SampleVariant | null>(offer === "choice" ? null : offer);
  const [consent, setConsent] = useState(false);
  const [website, setWebsite] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<string | null>(null);
  const [sentVariant, setSentVariant] = useState<SampleVariant | null>(null);

  // Move focus to the confirmation once React has rendered it, so keyboard and
  // screen-reader users hear it (a timer could fire before the heading exists).
  useEffect(() => {
    if (status === "sent") statusRef.current?.focus();
  }, [status]);

  const single = offer === "choice" ? null : SAMPLE_OFFER_COPY[offer];
  const eyebrow = single ? single.eyebrow : CHOICE_OFFER_COPY.eyebrow;
  const title = single ? single.title : CHOICE_OFFER_COPY.title;
  const body = single ? single.body : CHOICE_OFFER_COPY.body;
  const note = single ? single.note : CHOICE_OFFER_COPY.note;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (status === "submitting") return;
    if (!variant) {
      setError(SAMPLE_OFFER_ERRORS.choose);
      setStatus("error");
      return;
    }
    if (!LOOKS_LIKE_EMAIL.test(email.trim())) {
      setError(SAMPLE_OFFER_ERRORS.invalid_email);
      setStatus("error");
      return;
    }
    setStatus("submitting");
    setError(null);
    const outcome = await requestBlogSample({ email, variant, sourcePath, marketingConsent: consent, website });
    if (outcome === "sent") {
      setSentVariant(variant);
      setStatus("sent");
      return;
    }
    setError(
      outcome === "invalid_email"
        ? SAMPLE_OFFER_ERRORS.invalid_email
        : outcome === "rate_limited"
          ? SAMPLE_OFFER_ERRORS.rate_limited
          : SAMPLE_OFFER_ERRORS.failed,
    );
    setStatus("error");
  }

  const submitting = status === "submitting";

  return (
    <section
      aria-labelledby={headingId}
      data-pt-sample-offer={offer}
      data-pt-sample-placement={placement}
      className={`relative rounded-2xl border border-orange-200/80 bg-gradient-to-br from-[#fffaf4] via-white to-white shadow-[0_12px_32px_-20px_rgba(122,78,45,0.45)] overflow-hidden ${
        placement === "inline" ? "my-8" : ""
      }`}
    >
      <div className={single ? (popup ? "grid sm:grid-cols-[300px_1fr]" : "grid sm:grid-cols-[176px_1fr]") : ""}>
        {single && (
          <div className={popup ? "hidden sm:flex items-center justify-center bg-orange-50 border-r border-orange-100 p-8" : "hidden sm:flex items-start justify-center bg-orange-50/70 border-r border-orange-100 p-5"}>
            <img
              src={single.image.src}
              srcSet={single.image.srcSet}
              sizes={popup ? "236px" : "136px"}
              width={136}
              height={176}
              loading="lazy"
              decoding="async"
              alt={single.image.alt}
              className={`${popup ? "w-[236px]" : "w-[136px]"} h-auto rounded-lg shadow-md ring-1 ring-black/5`}
            />
          </div>
        )}

        <div className={popup ? "p-6 pt-14 sm:p-8 sm:pt-12" : "p-5 sm:p-7"}>
          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-orange-600 flex items-center gap-1.5">
            <i className="ri-file-pdf-2-line text-sm" aria-hidden="true"></i>
            {eyebrow}
          </p>
          <h2 id={headingId} className={`${popup ? "text-2xl sm:text-3xl" : "text-lg sm:text-xl"} mt-1.5 font-extrabold text-gray-900 leading-snug`}>
            {title}
          </h2>

          {status === "sent" && sentVariant ? (
            <div role="status" aria-live="polite" className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
              <h3
                ref={statusRef}
                tabIndex={-1}
                className="text-sm font-bold text-emerald-900 flex items-center gap-2 outline-none"
              >
                <i className="ri-mail-check-line text-base" aria-hidden="true"></i>
                Check your inbox
              </h3>
              <p className="mt-1.5 text-sm text-emerald-900/90 leading-relaxed">
                Your free 15-page {SAMPLE_OFFER_COPY[sentVariant].shortName} is on its way as a PDF attachment. It
                usually arrives within a few minutes. If you do not see it, check your spam or promotions folder.
              </p>
              <p className="mt-2 text-xs text-emerald-900/70 leading-relaxed">
                Each sample is emailed once a day per address. Still missing? Write to{" "}
                <a href="mailto:hello@pawtenant.com" className="font-semibold underline">
                  hello@pawtenant.com
                </a>
                .
              </p>
            </div>
          ) : (
            <>
              <p className="mt-2 text-sm text-gray-600 leading-relaxed">{body}</p>
              {single && (
                <ul className="mt-3 space-y-1.5">
                  {single.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm text-gray-700">
                      <i className="ri-checkbox-circle-line text-orange-500 mt-0.5" aria-hidden="true"></i>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              )}

              <form className="mt-5" onSubmit={onSubmit} noValidate aria-busy={submitting}>
                {offer === "choice" && (
                  <fieldset className="mb-4">
                    <legend className="text-sm font-bold text-gray-900 mb-2">{CHOICE_OFFER_COPY.legend}</legend>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {(["esa", "psd"] as const).map((v) => {
                        const c = SAMPLE_OFFER_COPY[v];
                        const checked = variant === v;
                        return (
                          <label
                            key={v}
                            className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition-colors ${
                              checked ? "border-orange-400 bg-orange-50" : "border-gray-200 bg-white hover:border-orange-200"
                            }`}
                          >
                            <input
                              type="radio"
                              name={`${uid}-variant`}
                              value={v}
                              checked={checked}
                              onChange={() => {
                                setVariant(v);
                                if (status === "error") setError(null);
                              }}
                              className="mt-1 h-4 w-4 accent-orange-500"
                            />
                            <span className="min-w-0">
                              <span className="block text-sm font-bold text-gray-900">{c.shortName}</span>
                              <span className="block text-xs text-gray-500 leading-snug mt-0.5">{c.choiceHint}</span>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </fieldset>
                )}

                <label htmlFor={emailId} className="block text-sm font-bold text-gray-900 mb-1.5">
                  Email address
                </label>
                <div className={popup ? "flex flex-col gap-2.5" : "flex flex-col sm:flex-row gap-2.5"}>
                  <input
                    id={emailId}
                    type="email"
                    name="email"
                    inputMode="email"
                    autoComplete="email"
                    required
                    maxLength={254}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    aria-invalid={status === "error" && error === SAMPLE_OFFER_ERRORS.invalid_email}
                    aria-describedby={error ? errorId : undefined}
                    placeholder="you@email.com"
                    className="w-full min-w-0 flex-1 rounded-xl border border-gray-300 bg-white px-4 py-3 text-[15px] text-gray-900 placeholder:text-gray-400 focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-200"
                  />
                  <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-orange-500 px-5 py-3 text-sm font-bold text-white shadow-sm transition-colors hover:bg-orange-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-300 focus-visible:ring-offset-2 disabled:cursor-wait disabled:opacity-80"
                  >
                    {submitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin" aria-hidden="true"></i>
                        Sending…
                      </>
                    ) : (
                      <>
                        <i className="ri-mail-send-line" aria-hidden="true"></i>
                        Email me the free sample
                      </>
                    )}
                  </button>
                </div>

                {/* Honeypot — invisible to people, never focusable. */}
                <div aria-hidden="true" className="absolute -left-[10000px] top-auto h-px w-px overflow-hidden">
                  <label>
                    Website
                    <input
                      type="text"
                      name="website"
                      tabIndex={-1}
                      autoComplete="off"
                      value={website}
                      onChange={(e) => setWebsite(e.target.value)}
                    />
                  </label>
                </div>

                <div className="mt-3 flex items-start gap-2.5">
                  <input
                    id={consentId}
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-gray-300 accent-orange-500"
                  />
                  <label htmlFor={consentId} className="text-xs text-gray-600 leading-relaxed">
                    {MARKETING_CONSENT.text}
                  </label>
                </div>

                {error && (
                  <p id={errorId} role="alert" className="mt-3 flex items-start gap-1.5 text-sm font-semibold text-red-700">
                    <i className="ri-error-warning-line mt-0.5" aria-hidden="true"></i>
                    <span>{error}</span>
                  </p>
                )}

                <p className="mt-3 text-[11.5px] text-gray-500 leading-relaxed">
                  {SAMPLE_OFFER_FINE_PRINT}{" "}
                  <Link to="/privacy-policy" className="underline hover:text-gray-700">
                    Privacy policy
                  </Link>
                  .
                </p>
              </form>
            </>
          )}

          <p className="mt-4 border-t border-orange-100 pt-3 text-xs text-gray-500 leading-relaxed flex items-start gap-1.5">
            <i className="ri-information-line mt-0.5 text-gray-400" aria-hidden="true"></i>
            <span>{note}</span>
          </p>
        </div>
      </div>
    </section>
  );
}
